/**
 * 🐱 "Vest bar above the CME bar": on a closed chart bar, the thin Vest bar in
 * the volume pane visibly sticks out above its CME bar, as the pane is drawn
 * right now (issue #599; was a raw share >= X% until then, #592/#596).
 *
 * The decision reads the pane's real geometry: the Vest series'
 * priceToCoordinate(Vest bar top) against the CME histogram's
 * priceToCoordinate(CME volume). A smaller y is taller; cat when the Vest top
 * is more than CAT_EXTRUDE_TOLERANCE_PX above the CME top. It therefore works
 * in either vestScale mode (independent: own hidden scale; same: stacked on
 * the CME bar) and follows the pane's autoscale / visible range, so the set of
 * cats can change when the pane rescales. Nothing about the pane's drawing
 * changes; it is only read.
 *
 * The raw numbers stay in the tooltip: share = Vest units / CME contracts on
 * the bar, no unit conversion.
 *
 * Closure is the same as the other candle markers:
 * - Vest: every 2m bar in the chart bar `closed` by the engine and the last
 *   one reaching the chart bar's end (browser clock only as the fallback);
 * - CME: every chart bar before the one holding the forming candle
 *   (`closedCmeVolumes`, same cutoff as `closedCmeRelVols`).
 * The forming bar never gets a cat. Bars with no CME volume (closed / missing
 * / 0) or no Vest bar (gap, weekend) get no cat. Kline-derived Vest bars count.
 *
 * Display only: no alert, no push.
 */
import { aggregateVestBars, VEST_BAR_MS, type VestBar, type VestPoint, type VestSource } from './vestVolume';
import type { MarkerSide } from './vestSpikes';

/** Pixels the Vest top must clear the CME top by, so equal heights never flicker. */
export const CAT_EXTRUDE_TOLERANCE_PX = 1;

/**
 * The pure decision: does the Vest bar's top (y, px, down is positive) sit
 * visibly above the CME bar's top? null / non-finite (series hidden, pane not
 * laid out) is never a cat.
 */
export function vestBarExtrudes(
  vestTopY: number | null | undefined,
  cmeTopY: number | null | undefined,
  tolerancePx: number = CAT_EXTRUDE_TOLERANCE_PX,
): boolean {
  if (vestTopY == null || cmeTopY == null || !Number.isFinite(vestTopY) || !Number.isFinite(cmeTopY)) return false;
  return vestTopY < cmeTopY - tolerancePx;
}

/** One drawn volume-pane bar: the Vest datum's top (as drawn) + the CME histogram value. */
export interface DrawnPaneBar {
  /** Chart candle open, seconds. */
  time: number;
  /** The Vest series value at the bar's top (`VestBarDatum.top`); undefined = no Vest bar. */
  vestTop?: number;
  /** The CME histogram value drawn for this candle. */
  cme: number;
}

export type ValueToY = (value: number) => number | null;

/**
 * Cat slots (seconds) whose Vest bar sticks out above the CME bar, judged
 * with the series' own value -> y functions. Bars outside the visible range
 * are judged against the current scale too (the functions map values, not
 * bars). On a 1m chart a 2m Vest bar is drawn on both 1m candles of its slot;
 * the slot (keyed at the 2m open) counts when it sticks out on either.
 */
export function extrudingCatSlots(
  drawn: Iterable<DrawnPaneBar>,
  vestY: ValueToY,
  cmeY: ValueToY,
  timeframeMinutes: number,
  tolerancePx: number = CAT_EXTRUDE_TOLERANCE_PX,
): Set<number> {
  const sub2m = timeframeMinutes * 60_000 < VEST_BAR_MS;
  const out = new Set<number>();
  for (const d of drawn) {
    if (typeof d.vestTop !== 'number' || !Number.isFinite(d.vestTop)) continue;
    const key = sub2m ? (Math.floor((d.time * 1000) / VEST_BAR_MS) * VEST_BAR_MS) / 1000 : d.time;
    if (out.has(key)) continue;
    if (vestBarExtrudes(vestY(d.vestTop), cmeY(d.cme || 0), tolerancePx)) out.add(key);
  }
  return out;
}

export interface VestCat {
  /** Chart bar open, seconds. */
  time: number;
  /** Raw Vest NQ-PERP units on the bar. */
  vest: number;
  /** Raw CME contracts of the chart symbol on the bar. */
  cme: number;
  /** vest / cme. */
  share: number;
  /** Vest taker buy - sell; null when (partly) kline-derived. */
  delta: number | null;
  src: VestSource;
  /** Below on Vest net selling, otherwise above (same rule as 🐋). */
  side: MarkerSide;
}

/**
 * Vest volume per CLOSED chart bar (seconds -> point). On a 1m chart the key
 * is the 2m bar's open (the bar a 1m candle shows).
 */
export function closedVestPoints(bars: Iterable<VestBar>, timeframeMinutes: number, nowMs: number): Map<number, VestPoint> {
  const sorted = [...bars].sort((a, b) => a.t - b.t);
  const intervalMs = Math.max(VEST_BAR_MS, Math.round(timeframeMinutes * 60_000));
  const sub2m = timeframeMinutes * 60_000 < VEST_BAR_MS;
  const flags = new Map<number, { open: boolean; closedTo: number }>();
  for (const b of sorted) {
    const key = Math.floor((sub2m ? b.t : Math.floor(b.t / intervalMs) * intervalMs) / 1000);
    const f = flags.get(key) ?? { open: false, closedTo: 0 };
    if (!b.closed) f.open = true;
    else f.closedTo = Math.max(f.closedTo, b.t + VEST_BAR_MS);
    flags.set(key, f);
  }
  const out = new Map<number, VestPoint>();
  for (const p of aggregateVestBars(sorted, timeframeMinutes).values()) {
    const f = flags.get(p.time);
    const end = p.time * 1000 + intervalMs;
    if (!f || f.open) continue;
    // The engine's clock first; the browser clock only for a bar a closure cut short.
    if (f.closedTo < end && end > nowMs) continue;
    out.set(p.time, p);
  }
  return out;
}

export interface CmeVolumeBar {
  /** Chart bar open, seconds. */
  time: number;
  volume: number;
}

/**
 * CME volume of every CLOSED chart bar (seconds -> contracts). `bars` = the
 * chart bars, `formingMs` = the forming base candle's open: every chart bar
 * before the one holding it is closed (as `closedCmeRelVols`); without a
 * forming candle the clock decides. On a 1m chart the two 1m candles of each
 * 2m slot are summed and keyed at the 2m open, so they match the 2m Vest bar
 * that slot shows; the slot counts once both candles are closed.
 */
export function closedCmeVolumes(
  bars: readonly CmeVolumeBar[],
  formingMs: number | null | undefined,
  timeframeMinutes: number,
  nowMs: number = Date.now(),
): Map<number, number> {
  const intervalMs = Math.max(60_000, Math.round(timeframeMinutes * 60_000));
  const cutoff = typeof formingMs === 'number' && Number.isFinite(formingMs) ? Math.floor(formingMs / intervalMs) * intervalMs : nowMs;
  const bucketMs = Math.max(VEST_BAR_MS, intervalMs);
  const sub2m = intervalMs < VEST_BAR_MS;
  const out = new Map<number, number>();
  for (const b of bars) {
    const start = sub2m ? Math.floor((b.time * 1000) / VEST_BAR_MS) * VEST_BAR_MS : b.time * 1000;
    if (start + (sub2m ? bucketMs : intervalMs) > cutoff) continue;
    const key = start / 1000;
    out.set(key, (out.get(key) ?? 0) + (b.volume || 0));
  }
  return out;
}

/**
 * Cats on every chart bar closed on both venues whose Vest bar sticks out
 * above the CME bar (`extruding`, from `extrudingCatSlots`). `cmeVolumes` from
 * `closedCmeVolumes`. Sorted by time.
 */
export function vestCats(
  vestBars: Iterable<VestBar>,
  cmeVolumes: ReadonlyMap<number, number>,
  timeframeMinutes: number,
  extruding: ReadonlySet<number>,
  nowMs: number = Date.now(),
): VestCat[] {
  const out: VestCat[] = [];
  if (extruding.size === 0) return out;
  for (const p of closedVestPoints(vestBars, timeframeMinutes, nowMs).values()) {
    if (!extruding.has(p.time)) continue;
    const cme = cmeVolumes.get(p.time);
    if (cme == null || !Number.isFinite(cme) || cme <= 0 || !Number.isFinite(p.v)) continue;
    out.push({ time: p.time, vest: p.v, cme, share: p.v / cme, delta: p.delta, src: p.src, side: p.delta !== null && p.delta < 0 ? 'below' : 'above' });
  }
  return out.sort((a, b) => a.time - b.time);
}

const int = (v: number): string => Math.round(v).toLocaleString('en-US');
const units = (v: number): string => v.toLocaleString('en-US', { maximumFractionDigits: 2 });
const pct = (s: number): string => `${(s * 100).toFixed(s < 0.1 ? 1 : 0)}%`;

/** Hover text: "🐱 Vest 1,598 vs CME 2,977 (54%), vest bar above cme bar". */
export function vestCatTooltip(c: VestCat, contract: string): { title: string; lines: string[] } {
  const lines = [
    `Vest NQ-PERP ${units(c.vest)} units vs CME ${contract} ${int(c.cme)} contracts`,
    c.delta == null
      ? 'Vest delta: n/a (kline-derived)'
      : `Vest delta ${c.delta >= 0 ? '+' : ''}${c.delta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (taker buy - sell)`,
    'Vest bar sticks out above the CME bar in the volume pane, as currently scaled. Raw volume, no unit conversion. Closed bar only. Descriptive, not a signal.',
  ];
  return { title: `🐱 Vest ${int(c.vest)} vs CME ${int(c.cme)} (${pct(c.share)}), vest bar above cme bar`, lines };
}
